import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, inject, Loader } from 'Mri.Web.Common';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { AdminStore } from './AdminStore';

export interface IMainSettingsProps extends RouteComponentProps<undefined> {}

@autoinject
@observer
export class MainSettings extends React.Component<IMainSettingsProps> {
    public render() {
        return (
            <div className="container">
                <div className="card">
                    <div className="card-body">
                        <a onClick={this.fullRefresh} className="btn btn-primary mb-3">
                            <Loader loading={this.loading} message="Refreshing..." />
                            {!this.loading && <span>Full Refresh</span>}
                        </a>
                        <p className="fst-italic">
                            This will trigger a background task to clear and re-read all cached objects in the
                            orleans
                            cluster. This will cause disruptions in availability of various API functions during the
                            refresh. Use sparingly. May cause timeout exceptions. This excludes Zendesk tickets.
                        </p>

                        <a onClick={this.syncSoftwareVersions} className="btn btn-primary mb-3">
                            <Loader loading={this.loading} message="Syncing..."/>
                            {!this.loading && <span>Sync Software Versions</span>}
                        </a>
                        <p className="fst-italic">This will trigger a background task to sync software versions.</p>

                        <button onClick={this.refreshShipDates} className="btn btn-primary mb-3">
                            <Loader loading={this.loading} message="Refreshing..." />
                            {!this.loading && <span>Refresh Ship Dates</span>}
                        </button>
                        <p className="fst-italic">
                            This will trigger a background task to clear and refresh all ShipDates contracts in SmartVu.
                            It's harmless and quite quick. Perform this task whenever SmartVu's ship dates don't match
                            PULTT.
                        </p>

                        <button onClick={this.reimportContracts} className="btn btn-danger mb-3" disabled>
                            <Loader loading={this.loading} message="Refreshing..." />
                            {!this.loading && <span>FourthShift ReImport</span>}
                        </button>
                        <p className="fst-italic">
                            This will trigger a background task to clear and re-read all FourthShift contracts in
                            SmartVu. This will cause disruptions in availability of various API functions during the
                            refresh. This will overwrite information if entered differently in FourthShift than SmartVu.
                        </p>

                        <a onClick={this.release} className="btn btn-primary mb-3">
                            <Loader loading={this.loading} message="Refreshing..." />
                            {!this.loading && <span>Release Locked Resources</span>}
                        </a>
                        <p className="fst-italic">
                            This will trigger a background task to release all locked resources (Files and Ports) in the
                            orleans cluster.
                        </p>

                        <button onClick={this.init} className="btn btn-primary mb-3">
                            <Loader loading={this.loading} message="Refreshing..." />
                            {!this.loading && <span>Init Orleans Schedulers</span>}
                        </button>
                        <p className="fst-italic">This will trigger a background task to start all job scheduling actors.</p>
                    </div>
                </div>
            </div>
        );
    }

    @bind
    @action
    private async fullRefresh(event: React.MouseEvent<HTMLAnchorElement>) {
        this.loading = true;

        try {
            await this.adminStore.refresh('orleans');
        } catch (e: any) {
            console.log(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private async syncSoftwareVersions(event: React.MouseEvent<HTMLAnchorElement>) {
        this.loading = true;

        try {
            await this.adminStore.syncSoftwareVersions();
        } catch (e: any) {
            console.log(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private async refreshShipDates() {
        this.loading = true;

        try {
            await this.adminStore.refresh('shipdates');
        } catch (e: any) {
            console.log(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private async reimportContracts() {
        this.loading = true;

        try {
            await this.adminStore.refresh('contracts');
        } catch (e: any) {
            console.log(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private async release(event: React.MouseEvent<HTMLAnchorElement>) {
        this.loading = true;

        try {
            await this.adminStore.refresh('release');
        } catch (e: any) {
            console.log(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private async init() {
        this.loading = true;

        try {
            await this.adminStore.refresh('init');
        } catch (e: any) {
            console.log(e);
        } finally {
            this.loading = false;
        }
    }

    @observable
    private loading: boolean = false;
    @inject
    private adminStore!: AdminStore;
}
